import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon,
  Clock,
  Video,
  Users,
  ChevronLeft,
  ChevronRight,
  Plus,
  Check,
  X,
  RefreshCw,
  Settings,
  Mail,
  Phone,
  Building2,
  ExternalLink,
  Copy,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// =============================================================================
// CONSTANTS
// =============================================================================

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const STATUS_COLORS = {
  scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  confirmed: 'bg-neon-green/20 text-neon-green border-neon-green/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  completed: 'bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30',
  no_show: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
};

const MEETING_TYPES = {
  demo: { label: 'Product Demo', duration: 30, icon: '🎯' },
  consultation: { label: 'Consultation', duration: 45, icon: '💼' },
  followup: { label: 'Follow-up Call', duration: 15, icon: '📞' },
  video_meeting: { label: 'Video Meeting', duration: 60, icon: '📹' },
  team_standup: { label: 'Team Standup', duration: 15, icon: '👥' }
};

// Generate Jitsi meeting URL
const generateJitsiUrl = (roomName) => {
  // Clean room name - remove special chars, replace spaces with dashes
  const cleanName = roomName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
  const uniqueId = Date.now().toString(36);
  return `https://meet.jit.si/medexova-${cleanName}-${uniqueId}`;
};

// =============================================================================
// STAT CARD
// =============================================================================

const StatCard = ({ icon: Icon, label, value, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-card p-4"
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm text-slate-400">{label}</div>
        <div className="text-2xl font-bold text-white">{value}</div>
      </div>
      <div className={`p-3 rounded-lg bg-${color}/20`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
    </div>
  </motion.div>
);

// =============================================================================
// EVENT CARD
// =============================================================================

const EventCard = ({ event, onStatusChange, onJoinMeeting }) => {
  const startDate = new Date(event.start);
  const [showActions, setShowActions] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="glass-card p-4 hover:bg-white/5 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[event.status]}`}>
              {event.status}
            </span>
            <span className="text-xs text-slate-500">
              {MEETING_TYPES[event.meeting_type]?.icon} {MEETING_TYPES[event.meeting_type]?.label}
            </span>
          </div>
          
          <h4 className="font-medium text-white mb-1">{event.title}</h4>
          
          <div className="flex items-center space-x-4 text-sm text-slate-400 mb-2">
            <span className="flex items-center">
              <Clock className="w-3.5 h-3.5 mr-1" />
              {startDate.toLocaleDateString()} at {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          
          <div className="flex items-center space-x-3 text-xs text-slate-500">
            <span className="flex items-center">
              <Users className="w-3 h-3 mr-1" />
              {event.guest_name}
            </span>
            {event.guest_company && (
              <span className="flex items-center">
                <Building2 className="w-3 h-3 mr-1" />
                {event.guest_company}
              </span>
            )}
            <span className="flex items-center">
              <Mail className="w-3 h-3 mr-1" />
              {event.guest_email}
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {event.meeting_link && event.status !== 'cancelled' && (
            <button
              onClick={() => onJoinMeeting(event.meeting_link)}
              className="px-3 py-1.5 bg-neon-purple/20 text-neon-purple rounded-lg text-sm hover:bg-neon-purple/30 transition-colors flex items-center"
            >
              <Video className="w-3.5 h-3.5 mr-1" />
              Join
            </button>
          )}
          
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4 text-slate-400" />
            </button>
            
            {showActions && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-10">
                {event.status === 'scheduled' && (
                  <>
                    <button
                      onClick={() => { onStatusChange(event.id, 'confirmed'); setShowActions(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-green-400 hover:bg-white/5 flex items-center"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirm
                    </button>
                    <button
                      onClick={() => { onStatusChange(event.id, 'cancelled'); setShowActions(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-white/5 flex items-center"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </>
                )}
                {(event.status === 'scheduled' || event.status === 'confirmed') && (
                  <>
                    <button
                      onClick={() => { onStatusChange(event.id, 'completed'); setShowActions(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-neon-cyan hover:bg-white/5 flex items-center"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Mark Complete
                    </button>
                    <button
                      onClick={() => { onStatusChange(event.id, 'no_show'); setShowActions(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-orange-400 hover:bg-white/5 flex items-center"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      No Show
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================================
// AVAILABILITY SETTINGS MODAL
// =============================================================================

const AvailabilityModal = ({ isOpen, onClose, settings, onSave }) => {
  const [formData, setFormData] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '10:00', end: '14:00' },
    sunday: { enabled: false, start: '10:00', end: '14:00' },
    meeting_duration: 30,
    buffer_minutes: 15
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (settings?.availability) {
      setFormData({
        ...settings.availability,
        meeting_duration: settings.meeting_duration || 30,
        buffer_minutes: settings.buffer_minutes || 15
      });
    }
  }, [settings]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
    onClose();
  };
  
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="glass-card p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-neon-cyan" />
          Availability Settings
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            {days.map(day => (
              <div key={day} className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData[day]?.enabled}
                    onChange={e => setFormData({
                      ...formData,
                      [day]: { ...formData[day], enabled: e.target.checked }
                    })}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-neon-cyan focus:ring-neon-cyan"
                  />
                  <span className="ml-2 text-white capitalize w-24">{day}</span>
                </label>
                
                {formData[day]?.enabled && (
                  <>
                    <input
                      type="time"
                      value={formData[day]?.start || '09:00'}
                      onChange={e => setFormData({
                        ...formData,
                        [day]: { ...formData[day], start: e.target.value }
                      })}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
                    />
                    <span className="text-slate-400">to</span>
                    <input
                      type="time"
                      value={formData[day]?.end || '17:00'}
                      onChange={e => setFormData({
                        ...formData,
                        [day]: { ...formData[day], end: e.target.value }
                      })}
                      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Meeting Duration</label>
              <select
                value={formData.meeting_duration}
                onChange={e => setFormData({ ...formData, meeting_duration: parseInt(e.target.value) })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Buffer Between</label>
              <select
                value={formData.buffer_minutes}
                onChange={e => setFormData({ ...formData, buffer_minutes: parseInt(e.target.value) })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
              >
                <option value={0}>No buffer</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
              </select>
            </div>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// MINI CALENDAR
// =============================================================================

const MiniCalendar = ({ selectedDate, onSelectDate, events }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };
  
  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.start.startsWith(dateStr));
  };
  
  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          className="p-1 hover:bg-white/10 rounded"
        >
          <ChevronLeft className="w-5 h-5 text-slate-400" />
        </button>
        <h3 className="font-medium text-white">
          {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          className="p-1 hover:bg-white/10 rounded"
        >
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 mb-2">
        {DAYS_OF_WEEK.map(day => (
          <div key={day}>{day}</div>
        ))}
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          const isSelected = selectedDate && 
            day.date.toDateString() === selectedDate.toDateString();
          const isToday = day.date.toDateString() === today.toDateString();
          const dayEvents = getEventsForDate(day.date);
          
          return (
            <button
              key={idx}
              onClick={() => onSelectDate(day.date)}
              className={`
                p-2 text-sm rounded-lg relative transition-colors
                ${day.isCurrentMonth ? 'text-white' : 'text-slate-600'}
                ${isSelected ? 'bg-neon-cyan text-black' : 'hover:bg-white/10'}
                ${isToday && !isSelected ? 'ring-1 ring-neon-cyan' : ''}
              `}
            >
              {day.date.getDate()}
              {dayEvents.length > 0 && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-neon-purple" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// BOOKING LINK CARD
// =============================================================================

const BookingLinkCard = () => {
  const [copied, setCopied] = useState(false);
  const bookingUrl = `${window.location.origin}/schedule`;
  
  const copyLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    toast.success('Booking link copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-medium text-white mb-2 flex items-center">
        <ExternalLink className="w-4 h-4 mr-2 text-neon-cyan" />
        Public Booking Link
      </h3>
      <p className="text-xs text-slate-400 mb-3">
        Share this link with prospects to let them self-schedule demos
      </p>
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={bookingUrl}
          readOnly
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
        />
        <button
          onClick={copyLink}
          className={`p-2 rounded-lg transition-colors ${
            copied ? 'bg-neon-green/20 text-neon-green' : 'bg-white/10 text-white hover:bg-white/20'
          }`}
        >
          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN CALENDAR COMPONENT
// =============================================================================

export const CalendarManager = () => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showSettings, setShowSettings] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showVideoMeeting, setShowVideoMeeting] = useState(false);
  const [videoMeetingForm, setVideoMeetingForm] = useState({
    title: '',
    participant_emails: ''
  });
  const [generatedMeetingLink, setGeneratedMeetingLink] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/events?limit=100`);
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  }, [API_URL]);
  
  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  // Fetch settings
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/availability`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEvents(), fetchStats(), fetchSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchEvents]);
  
  // Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchEvents(), fetchStats()]);
    setRefreshing(false);
  };
  
  // Update event status
  const handleStatusChange = async (eventId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/events/${eventId}/status?status=${status}`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast.success(`Event ${status}`);
        fetchEvents();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to update event');
    }
  };
  
  // Save availability settings
  const handleSaveSettings = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/availability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monday: formData.monday,
          tuesday: formData.tuesday,
          wednesday: formData.wednesday,
          thursday: formData.thursday,
          friday: formData.friday,
          saturday: formData.saturday,
          sunday: formData.sunday,
          timezone: 'America/New_York',
          meeting_duration: formData.meeting_duration,
          buffer_minutes: formData.buffer_minutes,
          blocked_dates: []
        })
      });
      
      if (response.ok) {
        toast.success('Availability updated!');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };
  
  // Join meeting
  const handleJoinMeeting = (url) => {
    window.open(url, '_blank');
  };
  
  // Create instant video meeting
  const createInstantMeeting = () => {
    const meetingTitle = videoMeetingForm.title || `MedexOVA Meeting ${new Date().toLocaleDateString()}`;
    const jitsiUrl = generateJitsiUrl(meetingTitle);
    setGeneratedMeetingLink(jitsiUrl);
    
    // Log to console for debugging
    console.log('Created Jitsi meeting:', jitsiUrl);
  };
  
  // Copy meeting link
  const copyMeetingLink = () => {
    if (generatedMeetingLink) {
      navigator.clipboard.writeText(generatedMeetingLink);
      toast.success('Meeting link copied to clipboard!');
    }
  };
  
  // Start meeting immediately
  const startMeetingNow = () => {
    if (generatedMeetingLink) {
      window.open(generatedMeetingLink, '_blank');
    }
  };
  
  // Reset video meeting modal
  const resetVideoMeetingModal = () => {
    setShowVideoMeeting(false);
    setVideoMeetingForm({ title: '', participant_emails: '' });
    setGeneratedMeetingLink(null);
  };
  
  // Filter events for selected date
  const selectedDateStr = selectedDate.toISOString().split('T')[0];
  const todayEvents = events.filter(e => e.start.startsWith(selectedDateStr));
  const upcomingEvents = events.filter(e => {
    const eventDate = new Date(e.start);
    return eventDate >= new Date() && e.status !== 'cancelled';
  }).slice(0, 5);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <CalendarIcon className="w-6 h-6 mr-2 text-neon-cyan" />
            Calendar
          </h1>
          <p className="text-slate-400 text-sm mt-1">Manage demo schedules and availability</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowVideoMeeting(true)}
            className="btn-secondary flex items-center bg-neon-purple/20 text-neon-purple hover:bg-neon-purple/30"
          >
            <Video className="w-4 h-4 mr-2" />
            Video Meeting
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="btn-primary flex items-center"
          >
            <Settings className="w-4 h-4 mr-2" />
            Availability
          </button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={CalendarIcon}
          label="Today"
          value={stats?.today || 0}
          color="neon-cyan"
        />
        <StatCard
          icon={Clock}
          label="This Week"
          value={stats?.this_week || 0}
          color="neon-purple"
        />
        <StatCard
          icon={Video}
          label="Upcoming"
          value={stats?.upcoming_events || 0}
          color="blue-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Completion Rate"
          value={`${stats?.completion_rate || 0}%`}
          color="neon-green"
        />
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Calendar + Booking Link */}
        <div className="space-y-4">
          <MiniCalendar
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            events={events}
          />
          <BookingLinkCard />
        </div>
        
        {/* Middle Column - Selected Date Events */}
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-card p-4">
            <h3 className="font-medium text-white mb-4">
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            
            {todayEvents.length > 0 ? (
              <div className="space-y-3">
                {todayEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onStatusChange={handleStatusChange}
                    onJoinMeeting={handleJoinMeeting}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400">No events scheduled for this date</p>
              </div>
            )}
          </div>
          
          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <div className="glass-card p-4">
              <h3 className="font-medium text-white mb-4 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-neon-purple" />
                Upcoming Events
              </h3>
              <div className="space-y-3">
                {upcomingEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onStatusChange={handleStatusChange}
                    onJoinMeeting={handleJoinMeeting}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <AvailabilityModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            settings={settings}
            onSave={handleSaveSettings}
          />
        )}
      </AnimatePresence>

      {/* Video Meeting Modal */}
      <AnimatePresence>
        {showVideoMeeting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={resetVideoMeetingModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="glass-card p-6 max-w-lg w-full"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                <Video className="w-5 h-5 mr-2 text-neon-purple" />
                Create Video Meeting
              </h3>
              
              {!generatedMeetingLink ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Meeting Title</label>
                    <input
                      type="text"
                      value={videoMeetingForm.title}
                      onChange={(e) => setVideoMeetingForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Team Standup, Client Demo, etc."
                      className="w-full input-dark"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Participant Emails (optional)</label>
                    <textarea
                      value={videoMeetingForm.participant_emails}
                      onChange={(e) => setVideoMeetingForm(prev => ({ ...prev, participant_emails: e.target.value }))}
                      placeholder="Enter emails separated by commas..."
                      rows={3}
                      className="w-full input-dark resize-none"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Invites will be sent via email with the meeting link
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4 border-t border-white/10">
                    <button
                      onClick={resetVideoMeetingModal}
                      className="px-4 py-2 text-slate-400 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createInstantMeeting}
                      className="px-4 py-2 bg-neon-purple text-white rounded-lg hover:bg-neon-purple/80 flex items-center"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Generate Meeting Link
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-neon-purple/10 rounded-lg border border-neon-purple/30">
                    <p className="text-sm text-slate-400 mb-2">Your meeting link is ready:</p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={generatedMeetingLink}
                        readOnly
                        className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
                      />
                      <button
                        onClick={copyMeetingLink}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                        title="Copy link"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={startMeetingNow}
                      className="py-3 bg-neon-green/20 text-neon-green rounded-lg hover:bg-neon-green/30 flex items-center justify-center"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Join Now
                    </button>
                    <button
                      onClick={copyMeetingLink}
                      className="py-3 bg-neon-cyan/20 text-neon-cyan rounded-lg hover:bg-neon-cyan/30 flex items-center justify-center"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </button>
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Powered by Jitsi Meet • No account required for participants
                  </p>
                  
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={resetVideoMeetingModal}
                      className="text-sm text-slate-400 hover:text-white"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarManager;
